import { useState, useEffect, type ReactNode } from 'react';
import authServiceInstance from '../api/authService';
import apiServiceInstance from '../api/apiService';
import { resetAuthRedirectFlag } from '../api/executer';
import { AuthContext, type AuthState, type AuthContextType } from './authContextTypes';
import { setDatadogUser, clearDatadogUser } from '../utils/datadog';
import { loadClientConfigs, clearConfigCache } from '../services/clientConfigService';
import { API_BASE_URL } from '../utils/constants';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initialize from localStorage
    const storedAuth = localStorage.getItem('authState');
    const initialState = storedAuth
      ? JSON.parse(storedAuth)
      : { user: null, token: null, isAuthenticated: false };

    // Set token in API service if available
    if (initialState.token) {
      apiServiceInstance.setToken(initialState.token);
    }

    // Set user info in Datadog for existing sessions
    if (initialState.user && initialState.isAuthenticated) {
      setDatadogUser({
        id: initialState.user.userId,
        email: initialState.user.email,
        name: initialState.user.name,
        role: initialState.user.role,
      });
    }

    return initialState;
  });

  // Load client configs and refresh user team info on mount if already authenticated (page refresh)
  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user) {
      loadClientConfigs(authState.token).catch(err => {
        console.warn('[Auth] Failed to load client configs on init:', err);
      });

      // Refresh team info from backend
      fetch(`${API_BASE_URL}/api/users/me`, {
        headers: {
          'Authorization': `Bearer ${authState.token}`,
        },
      })
        .then(res => res.json())
        .then(result => {
          if (result.success && result.data && authState.user) {
            const teamChanged = result.data.team !== authState.user.team;
            const teamDisplayNameChanged = result.data.teamDisplayName !== authState.user.teamDisplayName;
            
            // Update user team and display name if they changed
            if (teamChanged || teamDisplayNameChanged) {
              const updatedUser: typeof authState.user = {
                ...authState.user,
                team: result.data.team || null,
                teamDisplayName: result.data.teamDisplayName || null,
              };
              const updatedAuthState: AuthState = {
                ...authState,
                user: updatedUser,
              };
              setAuthState(updatedAuthState);
              localStorage.setItem('authState', JSON.stringify(updatedAuthState));
            }
          }
        })
        .catch(err => {
          console.warn('[Auth] Failed to refresh team info on init:', err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally using initial state

  useEffect(() => {
    // Listen for storage changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'authState') {
        const newAuthState = event.newValue
          ? JSON.parse(event.newValue)
          : { user: null, token: null, isAuthenticated: false };
        setAuthState(newAuthState);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    // Update localStorage when authState changes
    localStorage.setItem('authState', JSON.stringify(authState));

    // Update API service token
    apiServiceInstance.setToken(authState.token);
  }, [authState]);

  const login = async (email: string, password: string, recaptchaToken: string) => {
    // Call sign-in API
    const signinResult = await authServiceInstance.signIn({
      email,
      password,
      recaptchaToken,
    });

    if (!signinResult.success || !signinResult.data) {
      throw new Error('Sign-in failed. Please check your credentials.');
    }

    const { token } = signinResult.data;

    // Get user details
    const meResult = await authServiceInstance.me({ token });

    if (!meResult.success || !meResult.data) {
      throw new Error('Failed to fetch user details.');
    }

    const apiUser = meResult.data.user;

    // Fetch user info from backend to get team information and display name
    let team: string | null = null;
    let teamDisplayName: string | null = null;
    try {
      const backendUserResponse = await fetch(`${API_BASE_URL}/api/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (backendUserResponse.ok) {
        const backendUserResult = await backendUserResponse.json();
        if (backendUserResult.success && backendUserResult.data) {
          team = backendUserResult.data.team || null;
          teamDisplayName = backendUserResult.data.teamDisplayName || null;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch team info from backend:', err);
      // Continue without team - not critical for login
    }

    // Construct full name from firstName and lastName
    const user = {
      ...apiUser,
      name: `${apiUser.firstName} ${apiUser.lastName}`,
      team,
      teamDisplayName,
    };

    // Check if user belongs to an allowed account
    const getAllowedAccountIds = (): string[] => {
      // Support comma-separated list
      let allowedIds: string[] = [];
      const allowedIdsEnv = import.meta.env.VITE_ALLOWED_ACCOUNT_IDS;
      if (allowedIdsEnv) {
        allowedIds = allowedIdsEnv.split(',').map((id: string) => id.trim()).filter(Boolean);
      }

      // Always include VITE_FIA_ACCOUNT_ID if set (and not already in the list)
      const fiaAccountId = import.meta.env.VITE_FIA_ACCOUNT_ID;
      if (fiaAccountId) {
        const trimmedFiaId = fiaAccountId.trim();
        if (!allowedIds.includes(trimmedFiaId)) {
          allowedIds.push(trimmedFiaId);
        }
      }

      return allowedIds;
    };

    const allowedAccountIds = getAllowedAccountIds();
    if (allowedAccountIds.length > 0 && !allowedAccountIds.includes(user.accountId || '')) {
      throw new Error('Access denied. Your account is not authorized to access this portal.');
    }

    // Reset the 401 redirect flag (in case we're coming from an auto-redirect)
    resetAuthRedirectFlag();

    const newAuthState = {
      user,
      token,
      isAuthenticated: true,
    };

    // IMPORTANT: Update localStorage and API token IMMEDIATELY (before React's useEffect)
    // This prevents race conditions where API calls are made before the token is set
    localStorage.setItem('authState', JSON.stringify(newAuthState));
    apiServiceInstance.setToken(token);

    // Update React state (this triggers useEffect which will also update, but that's fine)
    setAuthState(newAuthState);

    // Set user info in Datadog for session tracking
    setDatadogUser({
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
    });

    // Load client configurations from backend (non-blocking)
    loadClientConfigs(token).catch(err => {
      console.warn('[Auth] Failed to load client configs:', err);
    });

    return user;
  };

  const logout = () => {
    // Clear token from API service
    apiServiceInstance.setToken(null);

    // Clear user info from Datadog
    clearDatadogUser();

    // Clear cached client configs
    clearConfigCache();

    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
    });
    localStorage.removeItem('authState');
  };

  const value: AuthContextType = {
    authState,
    setAuthState,
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

